// Analytics Tab - Pinterest/Metricool analytics dashboard
// Matches the design from the Python Flask version

import { useState, useEffect, useCallback, useRef } from "react";
import styles from "./AnalyticsTab.module.css";
import { apiUrl, authHeaders } from "../config";

// We'll load Chart.js dynamically
declare global {
  interface Window {
    Chart: any;
  }
}

interface Profile {
  profile_key?: string;
  user_id: number;
  blog_id: string;
  name: string;
  metricool_key_name: string;
}

const getProfileKey = (profile: Profile) =>
  profile.profile_key || `${profile.metricool_key_name}:${profile.blog_id}`;

interface Metrics {
  dates: string[];
  impressions: number[];
  clicks: number[];
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  trendImpressions: "up" | "down" | "neutral";
  trendClicks: "up" | "down" | "neutral";
}

interface TopPost {
  imageUrl: string;
  title: string;
  impressions: number;
  outboundClicks: number;
  saves: number;
  dateStr: string;
  timeStr: string;
}

interface AccountData {
  profile: Profile;
  metrics: Metrics | null;
  topPosts: TopPost[];
  loading: boolean;
  error: string | null;
}

export function AnalyticsTab() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [accountsData, setAccountsData] = useState<Map<string, AccountData>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartReady, setChartReady] = useState(() => Boolean(window.Chart));
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const chartRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const chartInstances = useRef<Map<string, unknown>>(new Map());

  // Date range - default to last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(thirtyDaysAgo.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);

  // Load Chart.js
  useEffect(() => {
    if (window.Chart) {
      setChartReady(true);
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-dishsnap-chartjs="true"]');
    const script = existingScript || document.createElement("script");
    const handleLoad = () => setChartReady(true);

    script.addEventListener("load", handleLoad);
    if (!existingScript) {
      script.src = "https://cdn.jsdelivr.net/npm/chart.js";
      script.async = true;
      script.dataset.dishsnapChartjs = "true";
      document.head.appendChild(script);
    }

    return () => script.removeEventListener("load", handleLoad);
  }, []);

  // Fetch all profiles on mount
  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(apiUrl("/api/metricool/analytics/profiles"), { headers: authHeaders() });
      if (!response.ok) throw new Error("Failed to fetch profiles");
      const data = await response.json();
      setProfiles(data.profiles || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load profiles");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccountData = useCallback(async (profile: Profile) => {
    const blogId = profile.blog_id;
    const profileKey = getProfileKey(profile);

    setAccountsData(prev => {
      const newMap = new Map(prev);
      newMap.set(profileKey, {
        profile,
        metrics: null,
        topPosts: [],
        loading: true,
        error: null,
      });
      return newMap;
    });

    try {
      const fromIso = `${fromDate}T00:00:00`;
      const toIso = `${toDate}T23:59:59`;

      const [metricsRes, postsRes] = await Promise.all([
        fetch(apiUrl(`/api/metricool/analytics/metrics?userId=${profile.user_id}&blogId=${blogId}&from=${fromIso}&to=${toIso}&keyName=${encodeURIComponent(profile.metricool_key_name)}`), { headers: authHeaders() }),
        fetch(apiUrl(`/api/metricool/analytics/top-posts?userId=${profile.user_id}&blogId=${blogId}&from=${fromIso}&to=${toIso}&limit=10&keyName=${encodeURIComponent(profile.metricool_key_name)}`), { headers: authHeaders() }),
      ]);

      const metricsData = await metricsRes.json();
      const postsData = await postsRes.json();

      setAccountsData(prev => {
        const newMap = new Map(prev);
        newMap.set(profileKey, {
          profile,
          metrics: metricsRes.ok ? metricsData : null,
          topPosts: postsRes.ok ? postsData.posts || [] : [],
          loading: false,
          error: !metricsRes.ok ? "Failed to load metrics" : null,
        });
        return newMap;
      });
    } catch (err) {
      setAccountsData(prev => {
        const newMap = new Map(prev);
        newMap.set(profileKey, {
          profile,
          metrics: null,
          topPosts: [],
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load data",
        });
        return newMap;
      });
    }
  }, [fromDate, toDate]);

  // Render charts when data changes
  useEffect(() => {
    if (!chartReady || !window.Chart) return;

    accountsData.forEach((data, profileKey) => {
      if (!data.metrics || data.loading) return;

      const canvas = chartRefs.current.get(profileKey);
      if (!canvas) return;

      // Destroy existing chart
      const existingChart = chartInstances.current.get(profileKey);
      if (existingChart && typeof (existingChart as { destroy: () => void }).destroy === "function") {
        (existingChart as { destroy: () => void }).destroy();
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const chart = new window.Chart(ctx, {
        type: "line",
        data: {
          labels: data.metrics.dates,
          datasets: [
            {
              label: "Impressions",
              data: data.metrics.impressions,
              borderColor: "#e60023",
              backgroundColor: "rgba(230,0,35,0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: 2,
              yAxisID: "y",
            },
            {
              label: "Outbound Clicks",
              data: data.metrics.clicks,
              borderColor: "#118060",
              borderWidth: 1,
              fill: false,
              tension: 0.3,
              pointRadius: 2,
              yAxisID: "y1",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "category",
              title: { display: true, text: "Date" },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10,
                maxRotation: 0,
              },
            },
            y: {
              type: "linear",
              beginAtZero: true,
              title: { display: true, text: "Impressions" },
              ticks: {
                callback: function(value: number | string) {
                  return Number(value).toLocaleString();
                },
              },
            },
            y1: {
              type: "linear",
              beginAtZero: true,
              position: "right",
              title: { display: true, text: "Outbound Clicks" },
              grid: { drawOnChartArea: false },
              ticks: {
                callback: function(value: number | string) {
                  return Number(value).toLocaleString();
                },
              },
            },
          },
          plugins: {
            legend: { position: "top" },
            tooltip: { mode: "index", intersect: false },
          },
        },
      });

      chartInstances.current.set(profileKey, chart);
    });
  }, [accountsData, chartReady]);

  const handleApplyDateRange = () => {
    selectedProfiles.forEach(profileKey => {
      const profile = profiles.find(p => getProfileKey(p) === profileKey);
      if (profile) {
        fetchAccountData(profile);
      }
    });
  };

  const handleToggleProfile = (profileKey: string) => {
    setSelectedProfiles(prev => {
      if (prev.includes(profileKey)) {
        return prev.filter(id => id !== profileKey);
      } else {
        const profile = profiles.find(p => getProfileKey(p) === profileKey);
        if (profile) {
          fetchAccountData(profile);
        }
        return [...prev, profileKey];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProfiles.length === profiles.length) {
      setSelectedProfiles([]);
      setAccountsData(new Map());
    } else {
      const allIds = profiles.map(getProfileKey);
      setSelectedProfiles(allIds);
      profiles.forEach(profile => fetchAccountData(profile));
    }
  };

  const togglePostsExpanded = (profileKey: string) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(profileKey)) {
        newSet.delete(profileKey);
      } else {
        newSet.add(profileKey);
      }
      return newSet;
    });
  };

  const formatNumber = (num: number) => num.toLocaleString();

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading analytics profiles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
        <button onClick={fetchProfiles} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Pinterest Analytics Dashboard</h1>
        <p className={styles.pageSubtitle}>
          View and analyze performance metrics across multiple Pinterest sources.
        </p>
      </div>

      {/* Date Range & Controls */}
      <div className={styles.controlsRow}>
        <div className={styles.dateRangeForm}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className={styles.dateInput}
          />
          <span className={styles.dateSeparator}>to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className={styles.dateInput}
          />
          <button onClick={handleApplyDateRange} className={styles.applyBtn}>
            Apply
          </button>
        </div>
        <button onClick={handleSelectAll} className={styles.selectAllBtn}>
          {selectedProfiles.length === profiles.length ? "Deselect All" : "Select All"}
        </button>
      </div>

      {/* Profile Selection */}
      <div className={styles.profilesRow}>
        {profiles.map(profile => {
          const profileKey = getProfileKey(profile);
          return (
            <button
              key={profileKey}
              className={`${styles.profileChip} ${selectedProfiles.includes(profileKey) ? styles.selected : ""}`}
              onClick={() => handleToggleProfile(profileKey)}
              title={`Metricool source: ${profile.metricool_key_name}`}
            >
              <span>{profile.name}</span>
              <small>{profile.metricool_key_name.replace("METRICOOL_API_KEY", "Key") || "Key"}</small>
            </button>
          );
        })}
      </div>

      {/* Account Cards - 2 columns like original */}
      <div className={styles.row}>
        {selectedProfiles.map(profileKey => {
          const data = accountsData.get(profileKey);
          if (!data) return null;

          return (
            <div key={profileKey} className={styles.colLg6}>
              <div className={styles.accountCard}>
                {/* Account Header */}
                <div className={styles.accountHeader}>
                  <h2 className={styles.accountName}>
                    <i className={styles.pinterestIcon}>P</i>
                    {data.profile.name}
                    <small className={styles.accountKeyName}>
                      {data.profile.metricool_key_name.replace("METRICOOL_API_KEY", "Key") || "Key"}
                    </small>
                  </h2>
                </div>

                {/* Account Body */}
                {data.loading ? (
                  <div className={styles.accountBody}>
                    <div className={styles.cardLoading}>Loading metrics...</div>
                  </div>
                ) : data.error ? (
                  <div className={styles.accountBody}>
                    <div className={styles.cardError}>{data.error}</div>
                  </div>
                ) : data.metrics ? (
                  <div className={styles.accountBody}>
                    {/* Stats Row */}
                    <div className={styles.statsRow}>
                      <div className={styles.statBox}>
                        <div className={styles.statTitle}>Impressions</div>
                        <div className={`${styles.statValue} ${styles.impressionsValue}`}>
                          {formatNumber(data.metrics.totalImpressions)}
                        </div>
                        <div className={`${styles.trendIndicator} ${styles[`trend${data.metrics.trendImpressions}`]}`}>
                          {data.metrics.trendImpressions === "up" && (
                            <><i className={styles.arrowUp}>↑</i> Increasing</>
                          )}
                          {data.metrics.trendImpressions === "down" && (
                            <><i className={styles.arrowDown}>↓</i> Decreasing</>
                          )}
                          {data.metrics.trendImpressions === "neutral" && (
                            <><i className={styles.arrowNeutral}>—</i> Stable</>
                          )}
                        </div>
                      </div>
                      <div className={styles.statBox}>
                        <div className={styles.statTitle}>Clicks</div>
                        <div className={`${styles.statValue} ${styles.clicksValue}`}>
                          {formatNumber(data.metrics.totalClicks)}
                        </div>
                        <div className={`${styles.trendIndicator} ${styles[`trend${data.metrics.trendClicks}`]}`}>
                          {data.metrics.trendClicks === "up" && (
                            <><i className={styles.arrowUp}>↑</i> Increasing</>
                          )}
                          {data.metrics.trendClicks === "down" && (
                            <><i className={styles.arrowDown}>↓</i> Decreasing</>
                          )}
                          {data.metrics.trendClicks === "neutral" && (
                            <><i className={styles.arrowNeutral}>—</i> Stable</>
                          )}
                        </div>
                      </div>
                      <div className={styles.statBox}>
                        <div className={styles.statTitle}>CTR (%)</div>
                        <div className={`${styles.statValue} ${styles.ctrValue}`}>
                          {data.metrics.ctr.toFixed(2)}%
                        </div>
                      </div>
                    </div>

                    {/* Chart Container */}
                    <div className={styles.chartContainer}>
                      <canvas
                        ref={(el) => {
                          if (el) chartRefs.current.set(profileKey, el);
                        }}
                      />
                    </div>

                    {/* Toggle Button for Top Posts */}
                    <button
                      className={`${styles.circleToggle} ${expandedPosts.has(profileKey) ? "" : styles.collapsed}`}
                      onClick={() => togglePostsExpanded(profileKey)}
                    >
                      <i className={styles.chevron}>▼</i>
                    </button>

                    {/* Top Posts (collapsible) */}
                    {expandedPosts.has(profileKey) && data.topPosts.length > 0 && (
                      <div className={styles.topPostsSection}>
                        <h3 className={styles.topPostsTitle}>
                          <i className={styles.trophyIcon}>🏆</i>
                          Top Performing Posts
                        </h3>
                        <div className={styles.postsGrid}>
                          {data.topPosts.map((post, idx) => (
                            <div key={idx} className={styles.postCard}>
                              {post.imageUrl && (
                                <img src={post.imageUrl} alt="" className={styles.postImg} />
                              )}
                              <div className={styles.postDetails}>
                                <div className={styles.postTitle}>{post.title}</div>
                                <div className={styles.postDate}>{post.dateStr} • {post.timeStr}</div>
                                <div className={styles.postMetrics}>
                                  <div className={`${styles.metric} ${styles.metricImpressions}`}>
                                    <i>👁</i> {formatNumber(post.impressions)}
                                  </div>
                                  <div className={`${styles.metric} ${styles.metricClicks}`}>
                                    <i>↗</i> {formatNumber(post.outboundClicks)}
                                  </div>
                                  <div className={`${styles.metric} ${styles.metricSaves}`}>
                                    <i>🔖</i> {formatNumber(post.saves)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {selectedProfiles.length === 0 && (
        <div className={styles.emptyState}>
          <p>Select Pinterest accounts above to view analytics</p>
        </div>
      )}
    </div>
  );
}
